import { trigger401Handler } from '@auth/auth-error-handler'
import { logOperation } from '@config/logging/frontend-logger'
import axios from 'axios'

import {
  createRequestId,
  createTraceparent,
  getRequestOrganizationId,
} from './request-correlation'
import {
  getCsrfToken,
  getDurationMs,
  getHeader,
  normalizeApiError,
  setHeader,
} from './utils'

const apiClient = axios.create({
  withCredentials: true,
})

apiClient.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {}

    const token = getCsrfToken()
    if (token) {
      setHeader(config.headers, 'X-CSRF-Token', token)
    }

    const requestId = createRequestId()
    const traceparent = createTraceparent()
    const organizationId =
      getHeader(config.headers, 'X-Organization-Id') ||
      getRequestOrganizationId()

    setHeader(config.headers, 'X-Request-ID', requestId)
    setHeader(config.headers, 'traceparent', traceparent)
    setHeader(config.headers, 'X-Organization-Id', organizationId)

    config.metadata = {
      ...config.metadata,
      requestId,
      traceparent,
      organizationId,
      startedAt: Date.now(),
      method: config.method,
      url: config.url,
    }

    return config
  },
  (error) => Promise.reject(error),
)

apiClient.interceptors.response.use(
  (response) => {
    if (response.config?.metadata) {
      response.config.metadata.durationMs = getDurationMs(
        response.config.metadata,
      )
    }

    if (response.config.raw) {
      return response
    }
    return response.data
  },
  async (error) => {
    const isCancelled = axios.isCancel(error) || error?.code === 'ERR_CANCELED'
    const structuredError = normalizeApiError(error)
    const metadata = error.config?.metadata

    if (structuredError.status === 401) {
      trigger401Handler()
    }

    if (metadata) {
      metadata.durationMs = getDurationMs(metadata)
    }

    if (!isCancelled) {
      logOperation({
        level: 'error',
        message: 'API request failed',
        component: 'api-client',
        functionName: 'response_interceptor',
        file: 'src/api/api-client.js',
        name: 'api_request',
        status: 'failed',
        ...(metadata?.correlationId && {
          correlationId: metadata.correlationId,
        }),
        request: metadata,
        httpStatus: structuredError.status,
        error: {
          type: structuredError.isNetworkError ? 'NetworkError' : 'ApiError',
          message: 'API request failed',
        },
        customProperties: {
          error_code: structuredError.code,
          is_network_error: structuredError.isNetworkError,
        },
      })
    }

    return Promise.reject(structuredError)
  },
)

export { apiClient }
